import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

console.log('[VerifyingToken MODULE] Module loaded/evaluated');

export function VerifyingToken() {
  console.log('[VerifyingToken] ===== FUNCTION CALLED - COMPONENT RENDERING =====');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { auth, verifyToken } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const hasAttemptedRef = useRef(false);

  const token = searchParams.get('token');

  console.log('[VerifyingToken] Component mounted/updated', {
    hasToken: !!token,
    tokenPreview: token?.substring(0, 20) + '...',
    hasAttempted: hasAttemptedRef.current,
    authState: {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
    },
  });

  useEffect(() => {
    console.log('[VerifyingToken] Verification useEffect triggered', {
      hasToken: !!token,
      hasAttempted: hasAttemptedRef.current,
    });

    if (!token) {
      console.log('[VerifyingToken] No token found, setting error');
      setError('No token provided');
      return;
    }

    if (hasAttemptedRef.current) {
      console.log('[VerifyingToken] Already attempted verification, skipping');
      return; // Prevent multiple calls
    }

    const verify = async () => {
      console.log('[VerifyingToken] Starting verification process...');
      setError(null);
      hasAttemptedRef.current = true;

      try {
        console.log('[VerifyingToken] Calling verifyToken...');
        await verifyToken(token);
        console.log('[VerifyingToken] verifyToken completed successfully');
        // Navigation will be handled by useEffect watching auth state
      } catch (err) {
        console.error('[VerifyingToken] Verification failed:', err);
        setError('Verification failed. Please try again or request a new magic link.');
      }
    };

    verify();
  }, [token, verifyToken, retryCount]);

  // Navigate to dashboard when authenticated
  useEffect(() => {
    console.log('[VerifyingToken] Navigation useEffect triggered', {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
    });

    if (auth.isAuthenticated && !auth.isLoading) {
      console.log('[VerifyingToken] User authenticated, navigating to /dashboard');
      console.log('[VerifyingToken] Auth state at navigation time:', {
        userId: auth.userId,
        jwt: auth.jwt?.substring(0, 20) + '...',
        user: auth.user,
      });
      navigate('/dashboard');
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate, auth.userId, auth.jwt, auth.user]);

  const handleRetry = () => {
    console.log('[VerifyingToken] Retry button clicked, resetting state');
    hasAttemptedRef.current = false;
    setError(null);
    setRetryCount((prev) => prev + 1);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Verification Error</CardTitle>
            <CardDescription>No token provided</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600">
              No verification token was found. Please check your email link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Verification Failed</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-red-600">{error}</p>
            <Button onClick={handleRetry} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Verifying</CardTitle>
          <CardDescription>Please wait while we verify your token...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
          <p className="text-sm text-center text-gray-600">Verifying your account...</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default VerifyingToken;
